import { useEffect } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { useCustomer } from "../contexts/CustomerContext";

let isRequestingLogin = false;

export const useBookingAuthInterceptor = () => {
  const { isAuthenticated, requestLogin, skipLogin, resetSkip, token } =
    useCustomer();

  useEffect(() => {
    // Add request interceptor to check auth before booking submission
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Only intercept POST requests to /api/bookings
        if (
          config.method === "post" &&
          config.url?.includes(`${API}/bookings`)
        ) {
          // If user explicitly skipped login, allow unauthenticated booking
          if (skipLogin) {
            resetSkip();
            return config;
          }

          // If not authenticated, prevent request and show login modal
          if (!isAuthenticated) {
            isRequestingLogin = true;
            requestLogin();
            // Cancel the request
            return Promise.reject(new axios.Cancel("User not authenticated"));
          }
          // If authenticated, add customer token
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to retry after login
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If request was cancelled due to auth requirement
        if (
          error.code === "ERR_CANCELED" &&
          isRequestingLogin &&
          isAuthenticated
        ) {
          isRequestingLogin = false;
          // Retry the original request
          const config = error.config;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return axios.request(config);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isAuthenticated, token, requestLogin, skipLogin, resetSkip]);
};

export default useBookingAuthInterceptor;
